import { RouteWithGates } from '../api/routes';
import { MultiGateComparison, MultiGateGroup, MultiGateSyncProgress } from '../types/MultiGate';

/**
 * One row of the Multi-gate page's gate list.
 *
 * The list is the reference session's own gate first (always on), then every
 * gate that already has a comparison session, then every other starred gate in
 * the team. A gate that has a comparison stays listed even if it has since been
 * unstarred, so it can still be unticked.
 */
export interface GateOption {
  id: string;
  label: string;
  routeDescription: string;
  routeDistance: number | null;
  pinned: boolean;
  isReference: boolean;
  comparison: MultiGateComparison | null;
}

export function gateLabel(
  name: string | null | undefined,
  notes: string | null | undefined,
  routeDistance: number | null | undefined,
): string {
  if (name) return name;
  if (notes) return notes;
  return routeDistance != null ? `${Math.round(routeDistance)} m` : 'gate';
}

export function buildGateOptions(routes: RouteWithGates[], group: MultiGateGroup): GateOption[] {
  const comparisonByGate = new Map<string, MultiGateComparison>();
  group.comparisons.forEach((c) => {
    if (c.gates_id) comparisonByGate.set(c.gates_id, c);
  });

  const options: GateOption[] = [];
  const seen = new Set<string>();
  const add = (option: GateOption) => {
    if (seen.has(option.id)) return;
    seen.add(option.id);
    options.push(option);
  };

  const refGate = group.reference.gate;
  if (refGate) {
    add({
      id: refGate.id,
      label: refGate.label,
      routeDescription: refGate.route_description ?? '',
      routeDistance: refGate.route_distance,
      pinned: refGate.pinned,
      isReference: true,
      comparison: null,
    });
  }

  group.comparisons.forEach((c) => {
    if (!c.gate) return;
    add({
      id: c.gate.id,
      label: c.gate.label,
      routeDescription: c.gate.route_description ?? '',
      routeDistance: c.gate.route_distance,
      pinned: c.gate.pinned,
      isReference: false,
      comparison: c,
    });
  });

  const starred: GateOption[] = [];
  routes.forEach((route) => {
    (route.gates ?? []).forEach((gate) => {
      if (!gate.pinned) return;
      starred.push({
        id: gate.id,
        label: gateLabel(gate.name, gate.notes, gate.route_distance),
        routeDescription: route.description,
        routeDistance: gate.route_distance,
        pinned: true,
        isReference: false,
        comparison: comparisonByGate.get(gate.id) ?? null,
      });
    });
  });
  starred
    .sort((a, b) => a.routeDescription.localeCompare(b.routeDescription))
    .forEach(add);
  return options;
}

/** Gate ids that currently have a comparison session. */
export function comparisonGateIds(group: MultiGateGroup): string[] {
  return group.comparisons.map((c) => c.gates_id).filter((id): id is string => !!id);
}

/** What the Multi-gate page's progress bar shows. */
export interface SyncProgressView {
  active: boolean;
  /** 0 to 1, or null while the sync job is copying rows and has no countable steps. */
  fraction: number | null;
  label: string;
  detail: string | null;
}

function failedNote(failed: number, total: number): string | null {
  if (failed === 0) return null;
  return `${failed} of ${total} comparison run${total === 1 ? '' : 's'} failed to process`;
}

/**
 * Turn a sync progress poll into what the bar shows. A sync runs in two parts: the sync job copies the
 * reference onto the comparisons (no count, so the bar is indeterminate and shows the job's stage), then
 * each comparison run reprocesses (the bar fills as they finish). Returns null when nothing is running and
 * nothing failed.
 */
export function syncProgressView(progress: MultiGateSyncProgress | undefined): SyncProgressView | null {
  if (!progress) return null;
  const { sync, runs } = progress;
  if (sync) {
    const verb = sync.rebuild ? 'Rebuild' : 'Synchronise';
    return {
      active: true,
      fraction: null,
      label: sync.status === 'queued' ? `${verb} queued` : `${verb}: ${sync.stage ?? 'starting'}`,
      detail: null,
    };
  }
  if (runs.pending > 0) {
    const done = runs.total - runs.pending;
    const waiting = runs.pending - runs.running;
    const parts = [`${runs.running} running`, `${waiting} waiting`];
    const failed = failedNote(runs.failed, runs.total);
    return {
      active: true,
      fraction: runs.total > 0 ? done / runs.total : 0,
      label: `Reprocessing comparison runs: ${done} of ${runs.total} done`,
      detail: failed ? `${parts.join(', ')}. ${failed}.` : `${parts.join(', ')}.`,
    };
  }
  const failed = failedNote(runs.failed, runs.total);
  if (!failed) return null;
  return { active: false, fraction: 1, label: 'Comparison runs processed', detail: `${failed}.` };
}
